import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Folder, GripVertical } from 'lucide-react';

import type {
  AnyTreeNode,
  TreeDropAction,
  TreeDropPosition,
  TreeDropZone,
} from '@/hooks';
import type {
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export const TREE_SCROLL_CONTAINER_ATTRIBUTE = 'data-tree-scroll-container';

const DRAG_ACTIVATION_DISTANCE_PX = 4;
const SIBLING_EDGE_ZONE_RATIO = 0.25;
const ROW_MIDPOINT_RATIO = 0.5;

type GetDropAction<F, L> = (
  active: AnyTreeNode<F, L>,
  target: AnyTreeNode<F, L>,
  zone: TreeDropZone,
) => TreeDropAction | null;

type DropHint = { overId: string; zone: TreeDropZone };

type DndState = {
  activeNode: AnyTreeNode | null;
  getDropAction: GetDropAction<unknown, unknown>;
  dropHint: DropHint | null;
};

const TreeDndStateContext = createContext<DndState>({
  activeNode: null,
  getDropAction: () => null,
  dropHint: null,
});

const getEventNode = <F, L>(
  data: { node?: unknown } | undefined,
): AnyTreeNode<F, L> | undefined => data?.node as AnyTreeNode<F, L> | undefined;

const getDropZone = (
  event: DragMoveEvent | DragEndEvent,
  active: AnyTreeNode | null,
): TreeDropZone | null => {
  const overRect = event.over?.rect;
  const overNode = getEventNode(event.over?.data.current);
  const activator = event.activatorEvent as Partial<PointerEvent>;
  if (!overRect || !overNode || typeof activator.clientY !== 'number') {
    return null;
  }
  const ratio =
    (activator.clientY + event.delta.y - overRect.top) / overRect.height;

  if (active?.kind === 'folder' && overNode.kind === 'folder') {
    if (ratio < SIBLING_EDGE_ZONE_RATIO) {
      return 'before';
    }
    return ratio > 1 - SIBLING_EDGE_ZONE_RATIO ? 'after' : 'into';
  }
  return ratio < ROW_MIDPOINT_RATIO ? 'before' : 'after';
};

type TreeDndContextProps<F, L> = {
  getDropAction: GetDropAction<F, L>;
  onDrop: (
    active: AnyTreeNode<F, L>,
    over: AnyTreeNode<F, L>,
    action: TreeDropAction,
    position: TreeDropPosition,
  ) => void;
  children: ReactNode;
};

export const TreeDndContext = <F, L>(props: TreeDndContextProps<F, L>) => {
  const { getDropAction, onDrop, children } = props;
  const [activeNode, setActiveNode] = useState<AnyTreeNode<F, L> | null>(null);
  const [dropHint, setDropHint] = useState<DropHint | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    }),
  );

  const onDragStart = (event: DragStartEvent) => {
    setActiveNode(getEventNode<F, L>(event.active.data.current) ?? null);
  };

  const onDragMove = (event: DragMoveEvent) => {
    const overNode = getEventNode<F, L>(event.over?.data.current);
    const zone = getDropZone(event, activeNode);
    setDropHint(overNode && zone ? { overId: overNode.id, zone } : null);
  };

  const onDragEnd = (event: DragEndEvent) => {
    const overNode = getEventNode<F, L>(event.over?.data.current);
    const zone = getDropZone(event, activeNode);
    if (activeNode && overNode && zone) {
      const action = getDropAction(activeNode, overNode, zone);
      if (action) {
        onDrop(
          activeNode,
          overNode,
          action,
          zone === 'after' ? 'after' : 'before',
        );
      }
    }
    setActiveNode(null);
    setDropHint(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      autoScroll={{
        canScroll: (element) =>
          element.hasAttribute(TREE_SCROLL_CONTAINER_ATTRIBUTE),
      }}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActiveNode(null);
        setDropHint(null);
      }}
    >
      <TreeDndStateContext.Provider
        value={{
          activeNode,
          getDropAction: getDropAction as GetDropAction<unknown, unknown>,
          dropHint,
        }}
      >
        {children}
      </TreeDndStateContext.Provider>
      <DragOverlay dropAnimation={null}>
        {activeNode ? <DragOverlayRow node={activeNode} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

const DragOverlayRow = (props: { node: AnyTreeNode }) => {
  const { node } = props;
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsCompact(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <div
      className={cn(
        'bg-accent text-accent-foreground ml-auto flex cursor-grabbing items-center gap-1.5 rounded-sm border py-0 pr-1 pl-1.5 text-sm shadow-md select-none',
        'transition-[max-width] duration-200 ease-standard',
        'min-h-8',
        isCompact ? 'max-w-48' : 'max-w-full',
      )}
    >
      {node.kind === 'folder' ? (
        <Folder className="text-muted-foreground size-4 shrink-0" />
      ) : null}
      <span className="flex-1 truncate">{node.label}</span>
    </div>
  );
};

type DraggableTreeNodeProps<F, L> = {
  node: AnyTreeNode<F, L>;
  children: (dragHandle: ReactNode) => ReactNode;
};

export const DraggableTreeNode = <F, L>(
  props: DraggableTreeNodeProps<F, L>,
) => {
  const { node, children } = props;
  const { t } = useTranslation();
  const { activeNode, getDropAction, dropHint } =
    useContext(TreeDndStateContext);

  const isDroppable =
    activeNode !== null &&
    (getDropAction(activeNode, node, 'before') !== null ||
      getDropAction(activeNode, node, 'into') !== null);

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id: node.id, data: { node } });

  const { setNodeRef: setDropRef } = useDroppable({
    id: node.id,
    data: { node },
    disabled: !isDroppable,
  });

  const setRefs = (element: HTMLDivElement | null) => {
    setDragRef(element);
    setDropRef(element);
  };

  const dragHandle = (
    <span
      {...attributes}
      {...listeners}
      role="button"
      aria-label={t('Drag to reorder')}
      className={cn(
        'text-muted-foreground hover:text-foreground flex items-center px-0.5',
        'cursor-grab touch-none',
      )}
    >
      <GripVertical className="size-3.5" />
    </span>
  );

  const hintZone = dropHint?.overId === node.id ? dropHint.zone : null;
  const hintAction =
    hintZone !== null && activeNode !== null
      ? getDropAction(activeNode, node, hintZone)
      : null;

  return (
    <div
      ref={setRefs}
      className={cn(
        'relative',
        isDragging && '[&_[role=treeitem]>*:not([aria-hidden])]:opacity-50',
        hintAction === 'into' && 'bg-accent/50 rounded-sm',
      )}
    >
      {hintAction === 'reorder' ? (
        <span
          aria-hidden
          className={cn(
            'bg-primary pointer-events-none absolute inset-x-0 z-10 h-0.5 rounded-full',
            hintZone === 'after' ? 'bottom-0' : 'top-0',
          )}
        />
      ) : null}
      {children(dragHandle)}
    </div>
  );
};
